import { useIntl, defineMessages } from 'react-intl';

import { Route } from 'react-router-dom';
import type { RouteComponentProps } from 'react-router-dom';

import ChatBubbleIcon from '@/material-icons/400-24px/chat_bubble.svg?react';
import CloseIcon from '@/material-icons/400-24px/close.svg?react';
import { Icon } from 'mastodon/components/icon';
import Bundle from 'mastodon/features/ui/components/bundle';
import { BundleColumnError } from 'mastodon/features/ui/components/bundle_column_error';
import { ColumnLoading } from 'mastodon/features/ui/components/column_loading';
import { Status } from 'mastodon/features/ui/util/async-components';

const messages = defineMessages({
  close: { id: 'threepane.close_post', defaultMessage: 'Close post' },
  label: { id: 'threepane.current_post', defaultMessage: 'Current post' },
  emptyTitle: {
    id: 'threepane.no_post_title',
    defaultMessage: 'No post selected',
  },
  emptyBody: {
    id: 'threepane.no_post_body',
    defaultMessage:
      'Pick a post from the timeline or from the people you follow to read it and its replies here.',
  },
});

type StatusComponent = React.FC<{
  params: Record<string, string>;
  multiColumn: boolean;
}>;

const renderLoading = () => <ColumnLoading multiColumn />;

const renderError = (props: { onRetry: () => void }) => (
  <BundleColumnError multiColumn errorType='network' {...props} />
);

const StatusRoute: React.FC<RouteComponentProps<Record<string, string>>> = ({
  match,
}) => (
  <Bundle fetchComponent={Status} loading={renderLoading} error={renderError}>
    {(StatusColumn: StatusComponent) => (
      <StatusColumn params={match.params} multiColumn />
    )}
  </Bundle>
);

/**
 * The post (with its thread) that is open right now. It is the stock <Status /> column, rendered
 * for the real location while the feed column keeps showing the location behind it.
 */
export const StatusPane: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const intl = useIntl();

  return (
    <section
      className='threepane__status'
      aria-label={intl.formatMessage(messages.label)}
    >
      <button
        type='button'
        className='threepane__status-close'
        onClick={onClose}
        title={intl.formatMessage(messages.close)}
        aria-label={intl.formatMessage(messages.close)}
      >
        <Icon id='times' icon={CloseIcon} />
      </button>
      <Route
        path={['/@:acct/:statusId', '/statuses/:statusId']}
        exact
        component={StatusRoute}
      />
    </section>
  );
};

/** The middle column while no post is open (wide layout only): says what the column is for. */
export const EmptyStatusPane: React.FC = () => {
  const intl = useIntl();

  return (
    <section
      className='threepane__status threepane__status--empty'
      aria-label={intl.formatMessage(messages.label)}
    >
      <div className='threepane__status-empty'>
        <Icon
          id='post'
          icon={ChatBubbleIcon}
          className='threepane__status-empty-icon'
        />
        <h2>{intl.formatMessage(messages.emptyTitle)}</h2>
        <p>{intl.formatMessage(messages.emptyBody)}</p>
      </div>
    </section>
  );
};
